import { createAction, createAsyncThunk } from "@reduxjs/toolkit";

import { IBxpWebStargateClient } from "@/libraries/bxp-web-stargate-client/src";
import { checkFeatureFlagGate } from "@/utils/feature-flags/check-feature-flag";
import { featureFlags } from "@/utils/feature-flags/feature-flag-manifest";

import { sendFastshiftSentryAlert } from "../common/utils/sentry-with-scope";

import { mockCustomerDomainEngagementProductionLinks } from "./mocks-for-actions";
import { getCustomerSupportRequestsQuery } from "./queries/get-customer-support-requests-query";
import { CustomerDomainOrg, Org, PageStateCache } from "./types";
import {
    transformAvailableProductsToCloudSitesByOrg,
    filterForActiveOrgs
} from "./utils";

const appEnvironment =
    process.env["NODE_ENV"] === "production" ? "production" : "staging";

export enum Actions {
    GET_USER_DETAILS = "[FASTSHIFT] GET_USER_DETAILS",
    GET_CUSTOMER_DOMAINS = "[FASTSHIFT] GET_CUSTOMER_DOMAINS",
    SET_SELECTED_ORG = "[FASTSHIFT] SET_SELECTED_ORG",
    SET_SELECTED_DOMAIN_ORG = "[FASTSHIFT] SET_SELECTED_DOMAIN_ORG",
    GET_ACCOUNT_ORGS = "[FASTSHIFT] GET_ACCOUNT_ORGS",
    GET_ORG_SERVERS = "[FASTSHIFT] GET_ORG_SERVERS",
    GET_MIGRATION_PLANS = "[FASTSHIFT] GET_MIGRATION_PLANS",
    GET_MIGRATION_PLAN_TOTALS = "[FASTSHIFT] GET_MIGRATION_PLAN_TOTALS",
    GET_AVAILABLE_PRODUCTS = "[FASTSHIFT] GET_AVAILABLE_PRODUCTS",
    GET_L1_ASSESSMENTS = "[FASTSHIFT] GET_L1_ASSESSMENTS",
    GET_ALL_CUSTOMER_SUPPORT_REQUESTS = "[FASTSHIFT] GET_ALL_CUSTOMER_SUPPORT_REQUESTS",
    SET_RESET_CUSTOMER_SUPPORT_REQUESTS = "[FASTSHIFT] SET_RESET_CUSTOMER_SUPPORT_REQUESTS",
    GET_INSTANCES = "[FASTSHIFT] GET_INSTANCES",
    GET_CUSTOMER_DOMAIN = "[FASTSHIFT] GET_CUSTOMER_DOMAIN",
    GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS = "[FASTSHIFT] GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS",
    CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE = "[FASTSHIFT] CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE",
    CLEAR_CUSTOMER_DOMAIN = "[FASTSHIFT] CLEAR_CUSTOMER_DOMAIN",
    GET_USER_TYPE = "[FASTSHIFT] GET_USER_TYPE",
    GET_FASTSHIFT_SUPPORT_REQUESTS = "[FASTSHIFT] GET_FASTSHIFT_SUPPORT_REQUESTS",
    SET_SELECTION_TABLE_CONTROLS = "[FASTSHIFT] SET_SELECTION_TABLE_CONTROLS",
    SET_MIGRATION_STATUS_TABLE_CONTROLS = "[FASTSHIFT] SET_MIGRATION_STATUS_TABLE_CONTROLS"
}

export const setSelectedOrg = createAction<Org>(Actions.SET_SELECTED_ORG);

export const setSelectedDomainOrg = createAction<CustomerDomainOrg>(
    Actions.SET_SELECTED_DOMAIN_ORG
);

export const getAccountOrgs = createAsyncThunk(
    Actions.GET_ACCOUNT_ORGS,
    async (stargateClient: IBxpWebStargateClient) => {
        const response = await stargateClient.getAccountOrgs({});

        const responseAdminhub = await stargateClient.getAdminhubAccountOrgs(
            {}
        );

        return filterForActiveOrgs(
            response?.accountOrgs,
            responseAdminhub?.accountOrgs
        );
    }
);

export const getAvailableProducts = createAsyncThunk(
    Actions.GET_AVAILABLE_PRODUCTS,
    async (stargateClient: IBxpWebStargateClient) => {
        const response = await stargateClient.getAvailableSitesForFastshift({});
        return transformAvailableProductsToCloudSitesByOrg(response);
    }
);

export const getOrgServers = createAsyncThunk(
    Actions.GET_ORG_SERVERS,
    async ({
        stargateClient,
        orgId
    }: {
        stargateClient: IBxpWebStargateClient;
        orgId: string;
    }) => {
        const response = await stargateClient.getOrgServers({
            orgId
        });

        return response;
    }
);

export const getMigrationPlanTotals = createAsyncThunk(
    Actions.GET_MIGRATION_PLAN_TOTALS,
    async ({
        stargateClient,
        orgId
    }: {
        stargateClient: IBxpWebStargateClient;
        orgId: string;
    }) => {
        const response = await stargateClient.getMigrationPlanTotals({
            orgId
        });

        return response;
    }
);

export const getL1Assessments = createAsyncThunk(
    Actions.GET_L1_ASSESSMENTS,
    async ({
        stargateClient,
        orgId
    }: {
        stargateClient: IBxpWebStargateClient;
        orgId: string;
    }) => {
        const response = await stargateClient.getL1Assessments({
            orgId
        });

        return response;
    }
);

// New simplified action that handles all pagination internally
export const getAllCustomerSupportRequests = createAsyncThunk(
    Actions.GET_ALL_CUSTOMER_SUPPORT_REQUESTS,
    async (stargateClient: IBxpWebStargateClient) => {
        const PAGE_SIZE = 50;

        let firstPageResponse = null;

        try {
            firstPageResponse = await getCustomerSupportRequestsQuery({
                stargateClient,
                page: 1,
                size: PAGE_SIZE
            });
        } catch (error) {
            console.error("Error fetching customer support requests", error);

            sendFastshiftSentryAlert(
                "Error on fetching customer support requests"
            );

            throw new Error(
                `Error fetching customer support requests: ${error}`
            );
        }

        if (firstPageResponse.errors?.length > 0) {
            throw new Error(
                `Error fetching customer support requests: ${firstPageResponse.errors
                    .map((error) => error.message)
                    .join(", ")}`
            );
        }

        const total = firstPageResponse.data.customerSupport.me.requests.total;
        const totalPages = Math.ceil(total / PAGE_SIZE);

        const requests = [
            ...firstPageResponse.data.customerSupport.me.requests.page.map(
                (request) => ({
                    ...request,
                    id: request.id.replace("GSAC-", "")
                })
            )
        ];

        if (totalPages < 1) {
            return {
                requests
            };
        }

        // Set this to series to avoid rate limiting issues, to improve performance we can create an array of promises and resolve all
        const pages = new Array(totalPages - 1).fill(0).map((_, i) => i + 2);

        for (const page of pages) {
            let pageResponse = null;

            try {
                pageResponse = await getCustomerSupportRequestsQuery({
                    stargateClient,
                    page,
                    size: PAGE_SIZE
                });
            } catch (error) {
                console.error(
                    "Error fetching customer support requests",
                    error
                );
                throw new Error("Error fetching customer support requests");
            }

            if (firstPageResponse.errors?.length > 0) {
                throw new Error("Error fetching customer support requests");
            }

            const pageRequests =
                pageResponse.data.customerSupport.me.requests.page.map(
                    (request) => ({
                        ...request,
                        id: request.id.replace("GSAC-", "")
                    })
                );

            requests.push(...pageRequests);
        }

        return {
            requests
        };
    }
);

export const setResetCustomerSupportRequests = createAction(
    Actions.SET_RESET_CUSTOMER_SUPPORT_REQUESTS
);

export const getInstances = createAsyncThunk(
    Actions.GET_INSTANCES,
    async ({
        stargateClient,
        orgId
    }: {
        stargateClient: IBxpWebStargateClient;
        orgId: string;
    }) => {
        const response = await stargateClient.getInstances({
            orgId
        });

        return response;
    }
);

export const getCustomerDomains = createAsyncThunk(
    Actions.GET_CUSTOMER_DOMAINS,
    async ({
        stargateClient,
        controls
    }: {
        stargateClient: IBxpWebStargateClient;
        controls: {
            page: number;
            size: number;
            sortBy?: string;
            sortDirection?: "ASC" | "DESC";
            searchTerm?: string;
        };
    }) => {
        const response = await stargateClient.getFastshiftCustomerDomains({
            ...controls
        });
        return response;
    }
);

export const getCustomerDomain = createAsyncThunk(
    Actions.GET_CUSTOMER_DOMAIN,
    async ({
        stargateClient,
        customerDomainId
    }: {
        stargateClient: IBxpWebStargateClient;
        customerDomainId: string;
    }) => {
        const response = await stargateClient.getFastshiftCustomerDomain({
            customerDomainId
        });
        return response.data;
    }
);

export const getCustomerDomainEngagementDetails = createAsyncThunk(
    Actions.GET_CUSTOMER_DOMAIN_ENGAGEMENT_DETAILS,
    async ({
        stargateClient,
        customerDomainId
    }: {
        stargateClient: IBxpWebStargateClient;
        customerDomainId: string;
    }) => {
        // temporary: for smart link validation in prod

        const isProductionEngagementCallEnabled = checkFeatureFlagGate(
            featureFlags.sxpFastshiftPortalEnableEngagementCallInProduction
        );
        if (
            appEnvironment === "production" &&
            !isProductionEngagementCallEnabled
        ) {
            return mockCustomerDomainEngagementProductionLinks;
        }
        const response =
            await stargateClient.getFastshiftCustomerDomainEngagement({
                customerDomainId
            });
        return response.data;
    }
);

export const clearAllCustomerDomainScopedState = createAction(
    Actions.CLEAR_ALL_CUSTOMER_DOMAIN_SCOPED_STATE
);

export const clearCustomerDomain = createAction(Actions.CLEAR_CUSTOMER_DOMAIN);

export const getUserDetails = createAsyncThunk(
    Actions.GET_USER_DETAILS,
    async (stargateClient: IBxpWebStargateClient) => {
        const response = await stargateClient.getFastshiftUser({});
        return response.data;
    }
);

export const getFastshiftSupportRequests = createAsyncThunk(
    Actions.GET_FASTSHIFT_SUPPORT_REQUESTS,
    async ({
        stargateClient,
        customerDomainId,
        orgId,
        permissionLevel
    }: {
        stargateClient: IBxpWebStargateClient;
        customerDomainId: string;
        orgId?: string;
        permissionLevel?: "ADMIN" | "NON_ADMIN";
    }) => {
        const response = await stargateClient.getFastshiftSupportRequests({
            customerDomainId,
            orgId,
            permissionLevel
        });

        return response;
    }
);

export const setSelectionTableControls = createAction<{
    selectionTableControls: PageStateCache["selectionTableControls"];
}>(Actions.SET_SELECTION_TABLE_CONTROLS);

export const setMigrationStatusTableControls = createAction<{
    migrationStatusTableControls: PageStateCache["migrationStatusTableControls"];
}>(Actions.SET_MIGRATION_STATUS_TABLE_CONTROLS);
